import {
  BarChart3,
  Bell,
  Brain,
  KeyRound,
  Lock,
  MonitorSmartphone,
  Palette,
  Plug,
  Puzzle,
  Shield,
  ShieldCheck,
  Sparkles,
  UserRound,
  type LucideIcon,
} from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { Commentable } from '@/shared/comments/Commentable';
import { useRole } from '@/versions/v1/shared/RoleContext';
import {
  SidebarRail,
  SidebarRailButton,
  SidebarRow,
  SidebarSection,
  SidebarShell,
} from './SidebarShell';
import { SidebarSceneHeader } from './SidebarSceneHeader';

type Section = {
  href: string;
  label: string;
  Icon: LucideIcon;
};

type SectionGroup = {
  label: string;
  items: Section[];
};

const GROUPS: SectionGroup[] = [
  {
    label: 'Personal',
    items: [
      { href: '/settings/account', label: 'Account', Icon: UserRound },
      { href: '/settings/notifications', label: 'Notifications', Icon: Bell },
      { href: '/settings/appearance', label: 'Appearance', Icon: Palette },
      { href: '/settings/privacy', label: 'Privacy', Icon: Lock },
      { href: '/settings/permissions', label: 'Permissions', Icon: Shield },
      {
        href: '/settings/linked-identities',
        label: 'Linked accounts',
        Icon: KeyRound,
      },
      {
        href: '/settings/install',
        label: 'Install app',
        Icon: MonitorSmartphone,
      },
    ],
  },
  {
    label: 'Tools',
    items: [
      { href: '/settings/connectors', label: 'Connectors', Icon: Plug },
      { href: '/settings/plugins', label: 'Plugins', Icon: Puzzle },
      { href: '/settings/skills', label: 'Skills', Icon: Sparkles },
    ],
  },
  {
    label: 'Agent',
    items: [
      { href: '/settings/memory', label: 'Memory', Icon: Brain },
      { href: '/settings/usage', label: 'Usage', Icon: BarChart3 },
    ],
  },
];

function isActive(pathname: string, href: string): boolean {
  if (pathname === href) return true;
  return pathname.startsWith(`${href}/`);
}

export function SettingsSidebar() {
  const { pathname } = useLocation();
  const { can } = useRole();
  const isAdmin = can('roles:admin');
  const railItems = GROUPS.flatMap((group) => group.items);

  return (
    <Commentable name="SettingsSidebar">
      <SidebarShell
        ariaLabel="Settings navigation"
        collapsedRail={
          <SidebarRail>
            {railItems.map((item) => (
              <SidebarRailButton
                key={item.href}
                href={item.href}
                icon={item.Icon}
                label={item.label}
                isActive={isActive(pathname, item.href)}
              />
            ))}
            {isAdmin ? (
              <SidebarRailButton
                href="/admin"
                icon={ShieldCheck}
                label="Admin panel"
                isActive={isActive(pathname, '/admin')}
              />
            ) : null}
          </SidebarRail>
        }
        header={
          <SidebarSceneHeader
            title="Settings"
            back={{ href: '/', label: 'Back to chats' }}
          />
        }
      >
        <nav aria-label="Settings sections">
          {GROUPS.map((group, gi) => (
            <SidebarSection key={group.label} label={group.label} first={gi === 0}>
              <ul className="space-y-0">
                {group.items.map((item) => (
                  <li key={item.href}>
                    <SidebarRow
                      href={item.href}
                      icon={item.Icon}
                      label={item.label}
                      isActive={isActive(pathname, item.href)}
                    />
                  </li>
                ))}
              </ul>
            </SidebarSection>
          ))}
          {isAdmin ? (
            <SidebarSection label="Admin">
              <ul className="space-y-0">
                <li>
                  <SidebarRow
                    href="/admin"
                    icon={ShieldCheck}
                    label="Admin panel"
                    isActive={isActive(pathname, '/admin')}
                  />
                </li>
              </ul>
            </SidebarSection>
          ) : null}
        </nav>
      </SidebarShell>
    </Commentable>
  );
}
